/**
 * Player Development chart PNG pack — dark PE chrome canvas charts + STORE zip.
 * Pure helpers (no React). No new deps.
 *
 * Zip: PD_{Casino}_{from}_{to}_charts.zip
 * Files: PD_{Casino}_{from}_{to}_G{n}.png
 */
import { downloadBlob } from './pdPdf';
import { sanitizePdCasinoToken } from './pdExcel';

export const PIE_COLORS = [
  '#5B8FF9',
  '#F6BD16',
  '#F6903D',
  '#61DDAA',
  '#7262fd',
  '#78D3F8',
  '#9661BC',
  '#008685',
];

export type PdChartBandVals = {
  use: number;
  visits: number;
  active: number;
  newUsers: number;
};

export type PdChartWeekPoint = {
  week: string;
  weekStart: string;
  use?: number;
  visits?: number;
  activeUseAndVisit?: number;
  newUsers?: number;
  byBand: Record<string, PdChartBandVals>;
};

export type PdChartMixSlice = { name: string; value: number; color: string };

export type PdChartPngInput = {
  casinoLabel: string;
  fromWeekStart: string;
  toWeekStart: string;
  ageBands: string[];
  bandColors: string[];
  weekSeries: PdChartWeekPoint[];
  g5Week: string;
  /** G5 views: Age / Gender / Gender counts by Age Range */
  g5Views: Array<{ key: string; title: string; mix: PdChartMixSlice[] }>;
  windows: {
    use: number[];
    visits: number[];
    useWowPct: number | null;
    useMomPct: number | null;
    visitsWowPct: number | null;
    visitsMomPct: number | null;
  };
};

const W = 960;
const H = 540;
const BG = '#121820';
const CARD = '#1a2332';
const TEXT = '#ffffff';
const MUTED = 'rgba(255,255,255,0.55)';
const GRID = 'rgba(255,255,255,0.08)';
const GOLD = '#F6BD16';

function makeCanvas(height: number = H): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = W;
  c.height = height;
  return c;
}

function paintChrome(
  ctx: CanvasRenderingContext2D,
  title: string,
  ageBands: string[],
  bandColors: string[]
) {
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, ctx.canvas.height);
  const ch = ctx.canvas.height;
  ctx.fillStyle = CARD;
  ctx.fillRect(16, 16, W - 32, ch - 32);

  ctx.fillStyle = GOLD;
  ctx.fillRect(32, 36, 8, 8);
  ctx.fillStyle = TEXT;
  ctx.font = 'bold 18px Inter, system-ui, sans-serif';
  ctx.fillText(title, 48, 46);

  ctx.font = '12px Inter, system-ui, sans-serif';
  let lx = 32;
  const ly = ch - 36;
  ageBands.forEach((band, i) => {
    const color = bandColors[i] || PIE_COLORS[i % PIE_COLORS.length];
    ctx.fillStyle = color;
    ctx.fillRect(lx, ly - 8, 10, 10);
    ctx.fillStyle = MUTED;
    ctx.fillText(band, lx + 14, ly);
    lx += ctx.measureText(band).width + 28;
  });
}

function niceMax(n: number): number {
  if (!Number.isFinite(n) || n <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(n)));
  const m = n / pow;
  const nice = m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10;
  return nice * pow;
}

function drawStackedBars(
  ctx: CanvasRenderingContext2D,
  title: string,
  series: PdChartWeekPoint[],
  ageBands: string[],
  bandColors: string[],
  metric: keyof PdChartBandVals
) {
  paintChrome(ctx, title, ageBands, bandColors);
  const plot = { x: 64, y: 72, w: W - 112, h: H - 140 };
  const months = series.map((s) => s.weekStart || s.week);
  const totals = series.map((s) =>
    ageBands.reduce((sum, b) => sum + (Number(s.byBand[b]?.[metric]) || 0), 0)
  );
  const yMax = niceMax(Math.max(...totals, 0) * 1.10);

  ctx.strokeStyle = GRID;
  ctx.lineWidth = 1;
  for (let i = 0; i <= 4; i++) {
    const y = plot.y + plot.h - (plot.h * i) / 4;
    ctx.beginPath();
    ctx.moveTo(plot.x, y);
    ctx.lineTo(plot.x + plot.w, y);
    ctx.stroke();
    ctx.fillStyle = MUTED;
    ctx.font = '11px Inter, system-ui, sans-serif';
    ctx.textAlign = 'right';
    const label = String(Math.round((yMax * i) / 4));
    ctx.fillText(label, plot.x - 8, y + 4);
  }
  ctx.textAlign = 'left';

  const n = Math.max(months.length, 1);
  const gap = 8;
  const barW = Math.max(8, (plot.w - gap * (n + 1)) / n);

  months.forEach((month, mi) => {
    const x = plot.x + gap + mi * (barW + gap);
    let yBase = plot.y + plot.h;
    ageBands.forEach((band, bi) => {
      const v = Number(series[mi].byBand[band]?.[metric]) || 0;
      const h = (v / yMax) * plot.h;
      ctx.fillStyle = bandColors[bi] || PIE_COLORS[bi % PIE_COLORS.length];
      ctx.fillRect(x, yBase - h, barW, h);
      yBase -= h;
    });
    ctx.fillStyle = MUTED;
    ctx.font = '10px Inter, system-ui, sans-serif';
    ctx.save();
    ctx.translate(x + barW / 2, plot.y + plot.h + 14);
    ctx.rotate(-Math.PI / 6);
    ctx.textAlign = 'right';
    ctx.fillText(month, 0, 0);
    ctx.restore();
  });
}

function drawDonut(
  ctx: CanvasRenderingContext2D,
  title: string,
  ageBands: string[],
  bandColors: string[],
  mix: PdChartMixSlice[],
  monthLabel: string
) {
  // Donut ABOVE (centered), legend BELOW full width — skip bottom age-band chrome legend
  paintChrome(ctx, `${title} · ${monthLabel}`, [], []);
  const cx = W / 2;
  const cy = 210;
  const rOut = 118;
  const rIn = 68;
  const total = mix.reduce((s, d) => s + d.value, 0) || 1;

  let angle = -Math.PI / 2;
  mix.forEach((slice) => {
    const sweep = (slice.value / total) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.fillStyle = slice.color;
    ctx.arc(cx, cy, rOut, angle, angle + sweep);
    ctx.closePath();
    ctx.fill();
    angle += sweep;
  });

  ctx.beginPath();
  ctx.fillStyle = CARD;
  ctx.arc(cx, cy, rIn, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = TEXT;
  ctx.font = 'bold 22px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(Math.round(total).toLocaleString(), cx, cy - 6);
  ctx.fillStyle = MUTED;
  ctx.font = '11px Inter, system-ui, sans-serif';
  ctx.fillText('players', cx, cy + 14);
  ctx.textAlign = 'left';

  // Full-width legend under the donut (wrap into columns)
  const legendTop = cy + rOut + 36;
  const cols = mix.length > 12 ? 3 : mix.length > 4 ? 2 : 1;
  const rowsPerCol = Math.ceil(mix.length / cols);
  const colW = (W - 96) / cols;
  mix.forEach((slice, i) => {
    const col = Math.floor(i / rowsPerCol);
    const row = i % rowsPerCol;
    const lx = 48 + col * colW;
    const ly = legendTop + row * 24;
    const pct = total > 0 ? (slice.value / total) * 100 : 0;
    ctx.fillStyle = slice.color;
    ctx.fillRect(lx, ly - 8, 12, 12);
    ctx.fillStyle = TEXT;
    ctx.font = '13px Inter, system-ui, sans-serif';
    ctx.fillText(slice.name, lx + 20, ly);
    ctx.fillStyle = MUTED;
    ctx.fillText(
      `${Math.round(slice.value).toLocaleString()} · ${pct.toFixed(1)}%`,
      lx + Math.min(160, colW * 0.5),
      ly
    );
  });
}


function drawSimpleBars(
  ctx: CanvasRenderingContext2D,
  title: string,
  series: PdChartWeekPoint[],
  metric: 'activeUseAndVisit' | 'newUsers',
  color: string
) {
  paintChrome(ctx, title, [], []);
  const plot = { x: 64, y: 72, w: W - 112, h: H - 140 };
  const labels = series.map((s) => s.weekStart || s.week);
  const vals = series.map((s) => Number(s[metric] || 0));
  const yMax = niceMax(Math.max(...vals, 0) * 1.10);
  ctx.strokeStyle = GRID;
  ctx.lineWidth = 1;
  for (let i = 0; i <= 4; i++) {
    const y = plot.y + plot.h - (plot.h * i) / 4;
    ctx.beginPath();
    ctx.moveTo(plot.x, y);
    ctx.lineTo(plot.x + plot.w, y);
    ctx.stroke();
    ctx.fillStyle = MUTED;
    ctx.font = '11px Inter, system-ui, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(String(Math.round((yMax * i) / 4)), plot.x - 8, y + 4);
  }
  ctx.textAlign = 'left';
  const n = Math.max(labels.length, 1);
  const gap = 8;
  const barW = Math.max(8, (plot.w - gap * (n + 1)) / n);
  labels.forEach((lab, i) => {
    const v = vals[i] || 0;
    const h = yMax ? (v / yMax) * plot.h : 0;
    const x = plot.x + gap + i * (barW + gap);
    ctx.fillStyle = color;
    ctx.fillRect(x, plot.y + plot.h - h, barW, Math.max(h, 0));
    ctx.fillStyle = MUTED;
    ctx.font = '10px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(String(lab).replace(/^[0-9]{4}-/, 'W'), x + barW / 2, plot.y + plot.h + 14);
  });
  ctx.textAlign = 'left';
}

function drawWindows(
  ctx: CanvasRenderingContext2D,
  title: string,
  values: number[],
  labels: string[],
  color: string,
  wow: number | null,
  mom: number | null
) {
  paintChrome(ctx, title, [], []);
  const plot = { x: 80, y: 80, w: W - 160, h: H - 200 };
  const sum = values.reduce((s, v) => s + v, 0);
  const yMax = niceMax(Math.max(...values, 0) * 1.10);
  const n = values.length;
  const gap = 28;
  const barW = Math.max(28, (plot.w - gap * (n + 1)) / n);
  values.forEach((v, i) => {
    const h = yMax ? (v / yMax) * plot.h : 0;
    const x = plot.x + gap + i * (barW + gap);
    ctx.fillStyle = color;
    ctx.fillRect(x, plot.y + plot.h - h, barW, Math.max(h, 0));
    ctx.fillStyle = TEXT;
    ctx.font = 'bold 14px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(Math.round(v).toLocaleString(), x + barW / 2, plot.y + plot.h - h - 8);
    ctx.fillStyle = MUTED;
    ctx.font = '12px Inter, system-ui, sans-serif';
    ctx.fillText(labels[i], x + barW / 2, plot.y + plot.h + 18);
    const pct = sum > 0 ? (v / sum) * 100 : 0;
    ctx.fillText(`${pct.toFixed(1)}%`, x + barW / 2, plot.y + plot.h + 34);
  });
  const fmt = (p: number | null) => (p == null ? '—' : `${p > 0 ? '▲' : p < 0 ? '▼' : ''} ${Math.abs(p)}%`);
  ctx.textAlign = 'left';
  ctx.font = 'bold 16px Inter, system-ui, sans-serif';
  ctx.fillStyle = wow == null ? MUTED : wow >= 0 ? '#3DDC97' : '#E07050';
  ctx.fillText(`WoW ${fmt(wow)}  ·  this week vs last week`, 64, H - 48);
  ctx.fillStyle = mom == null ? MUTED : mom >= 0 ? '#3DDC97' : '#E07050';
  ctx.fillText(`MoM ${fmt(mom)}  ·  MTD vs last month`, 64, H - 28);
}

async function canvasToPngBytes(canvas: HTMLCanvasElement): Promise<Uint8Array> {
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png');
  });
  return new Uint8Array(await blob.arrayBuffer());
}

function crc32(buf: Uint8Array): number {
  let c = ~0;
  for (let i = 0; i < buf.length; i++) {
    c ^= buf[i];
    for (let k = 0; k < 8; k++) c = (c >>> 1) ^ (0xedb88320 & -(c & 1));
  }
  return ~c >>> 0;
}

function u16(n: number): Uint8Array {
  return new Uint8Array([n & 0xff, (n >>> 8) & 0xff]);
}
function u32(n: number): Uint8Array {
  return new Uint8Array([n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff]);
}
function concat(parts: Uint8Array[]): Uint8Array {
  const len = parts.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(len);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

export function buildStoreZip(files: Array<{ name: string; data: Uint8Array }>): Blob {
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;
  const enc = new TextEncoder();

  for (const f of files) {
    const nameBytes = enc.encode(f.name);
    const crc = crc32(f.data);
    const size = f.data.length;
    const local = concat([
      u32(0x04034b50),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(size),
      u32(size),
      u16(nameBytes.length),
      u16(0),
      nameBytes,
      f.data,
    ]);
    const central = concat([
      u32(0x02014b50),
      u16(20),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(size),
      u32(size),
      u16(nameBytes.length),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(0),
      u32(offset),
      nameBytes,
    ]);
    locals.push(local);
    centrals.push(central);
    offset += local.length;
  }

  const localBlob = concat(locals);
  const centralBlob = concat(centrals);
  const end = concat([
    u32(0x06054b50),
    u16(0),
    u16(0),
    u16(files.length),
    u16(files.length),
    u32(centralBlob.length),
    u32(localBlob.length),
    u16(0),
  ]);
  const zipBytes = concat([localBlob, centralBlob, end]);
  const copy = new Uint8Array(zipBytes.byteLength);
  copy.set(zipBytes);
  return new Blob([copy], { type: 'application/zip' });
}

export function buildPdChartsZipFilename(casino: string, from: string, to: string): string {
  return `PD_${sanitizePdCasinoToken(casino)}_${from}_${to}_charts.zip`;
}

export function buildPdChartPngName(
  casino: string,
  from: string,
  to: string,
  n: number | string
): string {
  return `PD_${sanitizePdCasinoToken(casino)}_${from}_${to}_G${n}.png`;
}

export async function buildPlayerDevelopmentChartPngZip(input: PdChartPngInput): Promise<Blob> {
  const { ageBands, bandColors, weekSeries, casinoLabel, fromWeekStart, toWeekStart } = input;

  const jobs: Array<{
    n: number | string;
    height?: number;
    draw: (ctx: CanvasRenderingContext2D) => void;
  }> = [
    {
      n: 1,
      draw: (ctx) =>
        drawStackedBars(ctx, 'G1 · Use over weeks', weekSeries, ageBands, bandColors, 'use'),
    },
    {
      n: 2,
      draw: (ctx) =>
        drawStackedBars(ctx, 'G2 · Visits over weeks', weekSeries, ageBands, bandColors, 'visits'),
    },
    {
      n: 3,
      draw: (ctx) =>
        drawSimpleBars(ctx, 'G3 · Active Use & Visit over weeks', weekSeries, 'activeUseAndVisit', '#5B8FF9'),
    },
    {
      n: 4,
      draw: (ctx) =>
        drawSimpleBars(ctx, 'G4 · New Users over weeks', weekSeries, 'newUsers', '#5B8FF9'),
    },
  ];

  const g5Views = input.g5Views?.length ? input.g5Views : [];
  g5Views.forEach((view) => {
    const rowsGuess = Math.ceil(view.mix.length / (view.mix.length > 12 ? 3 : view.mix.length > 4 ? 2 : 1));
    const needH = Math.max(H, 364 + rowsGuess * 24 + 48);
    jobs.push({
      n: `5_${view.key}`,
      height: needH,
      draw: (ctx) =>
        drawDonut(ctx, view.title, ageBands, bandColors, view.mix, input.g5Week),
    });
  });

  if (input.windows) {
    jobs.push({
      n: '7_Use_windows',
      draw: (ctx) =>
        drawWindows(
          ctx,
          'G7 · Use windows',
          input.windows.use,
          ['Last Month', 'Last Week', 'Month to date', 'This week'],
          '#5B8FF9',
          input.windows.useWowPct,
          input.windows.useMomPct
        ),
    });
    jobs.push({
      n: '8_Visits_windows',
      draw: (ctx) =>
        drawWindows(
          ctx,
          'G8 · Visits windows',
          input.windows.visits,
          ['Last Month', 'Last Week', 'Month to date', 'This week'],
          '#F6BD16',
          input.windows.visitsWowPct,
          input.windows.visitsMomPct
        ),
    });
  }

  const files: Array<{ name: string; data: Uint8Array }> = [];
  for (const job of jobs) {
    const canvas = makeCanvas(job.height ?? H);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2d context unavailable');
    job.draw(ctx);
    const name =
      job.n === '7_Use_windows'
        ? 'G7_Use_windows.png'
        : job.n === '8_Visits_windows'
          ? 'G8_Visits_windows.png'
          : buildPdChartPngName(casinoLabel, fromWeekStart, toWeekStart, job.n);
    files.push({
      name,
      data: await canvasToPngBytes(canvas),
    });
  }
  return buildStoreZip(files);
}

export async function downloadPlayerDevelopmentChartPngZip(input: PdChartPngInput): Promise<void> {
  const blob = await buildPlayerDevelopmentChartPngZip(input);
  downloadBlob(blob, buildPdChartsZipFilename(input.casinoLabel, input.fromWeekStart, input.toWeekStart));
}

/** @deprecated alias */
export const downloadPlayerDevelopmentChartPngs = downloadPlayerDevelopmentChartPngZip;
export const buildPlayerDevelopmentChartPngs = buildPlayerDevelopmentChartPngZip;
